// Typed access to Vite environment. Anything missing falls back to a safe
// default so the app boots in Phase 0 without secrets configured.

type AppMode = 'development' | 'staging' | 'production' | 'test';

type AppEnv = {
  appUrl: string;
  mode: AppMode;
  isDev: boolean;
  isProd: boolean;
  supabase: {
    url: string | undefined;
    anonKey: string | undefined;
  };
  recaptchaSiteKey: string | undefined;
  googleClientId: string | undefined;
};

function resolveMode(): AppMode {
  const raw = import.meta.env.MODE;
  if (raw === 'staging' || raw === 'production' || raw === 'test') {
    return raw;
  }
  return 'development';
}

const mode = resolveMode();

// Production guard — reCAPTCHA verification is mandatory per Spec V1.1
// §M13.6. The dev-mode bypass in src/lib/recaptcha.ts skips the Edge
// Function call when the site key is empty; without this guard a missing
// VITE_RECAPTCHA_SITE_KEY in a Netlify production env would silently
// disable bot protection. Crashing the boot is intentional.
if (mode === 'production' && !import.meta.env.VITE_RECAPTCHA_SITE_KEY) {
  throw new Error(
    '[env] VITE_RECAPTCHA_SITE_KEY is required in production builds (Spec V1.1 §M13.6).',
  );
}

export const env: AppEnv = {
  appUrl: import.meta.env.VITE_APP_URL ?? 'http://localhost:5173',
  mode,
  isDev: mode === 'development',
  isProd: mode === 'production',
  supabase: {
    url: import.meta.env.VITE_SUPABASE_URL,
    anonKey: import.meta.env.VITE_SUPABASE_ANON_KEY,
  },
  recaptchaSiteKey: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
  googleClientId: import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID,
};
